import { useContext, useEffect, useMemo, useRef, useState } from 'react'

import { Loading } from '@hhgtech/hhg-components'
import { SelectProps, Text } from '@hhgtech/hhg-components/mantine'
import { ChevronDown } from '@hhgtech/icons/arrow'
import { CurrentLocation } from '@hhgtech/icons/core'
import { Box, Portal, Skeleton, Sx, Transition } from '@mantine/core'
import { useClickOutside } from '@mantine/hooks'
import { useIntl } from 'react-intl'
import { shallow } from 'zustand/shallow'

import { useIsMobile } from 'hooks/useIsMobile'
import { useAirPopulationStore } from 'state/airPopulation'

import useStyles from './index.styles'
import { Select } from './Select'
import {
  CURRENT_LOCATION_KEY,
  CURRENT_LOCATION_LABEL_TRANSLATE_KEY,
} from '../constants'
import { AirPopulationContext } from '../context'

export const LocationSelector = ({
  styles,
  classNames,
}: Partial<SelectProps>) => {
  const { formatMessage: f } = useIntl()
  const {
    data,
    selectedValue,
    setSelectedValue,
    loading,
    firstLoading,
    selectorZIndex,
    onChangeValue,
  } = useContext(AirPopulationContext)
  const { listProvinces } = useAirPopulationStore(
    (state) => ({
      listProvinces: state.listProvinces || [],
    }),
    shallow,
  )
  const provinceListOptions = useMemo(
    () => [
      {
        label: f({
          id: CURRENT_LOCATION_LABEL_TRANSLATE_KEY,
        }),
        value: CURRENT_LOCATION_KEY,
        isUseCurrentLocation: true,
        icon: CurrentLocation,
      },
      ...(listProvinces || []).map((i) => ({
        label: i,
        value: i,
      })),
    ],
    [listProvinces],
  )

  const [open, setOpen] = useState(false)
  const [dropdownPos, setDropdownPos] = useState<{
    top: number
    left: number
    width: number
  }>()

  const selectedValRef = useClickOutside(() => setOpen(false))
  const { classes } = useStyles(undefined, {
    name: 'AirPopulation__LocationSelector',
    styles,
    classNames,
  })

  const inputRef = useRef<HTMLDivElement>(null)
  const isMobile = useIsMobile()

  const handleOpen = () => {
    if (loading) return
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect()
      setDropdownPos({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
      })
    }
    setOpen(true)
  }

  useEffect(() => {
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect()
      setDropdownPos({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
      })
    }
  }, [firstLoading])

  useEffect(() => {
    if (data && inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect()
      setDropdownPos({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
      })
    }
  }, [data])

  useEffect(() => {
    setOpen(false)
  }, [isMobile])

  return (
    <Box className={classes.root}>
      {firstLoading ? (
        <Skeleton height={24} radius={4} width={140} />
      ) : (
        <Box className={classes.locator}>
          <Box className={classes.locatorValue} onClick={handleOpen}>
            <Text size="h3" variant="secondary">
              {selectedValue === CURRENT_LOCATION_KEY
                ? f({
                    id: CURRENT_LOCATION_LABEL_TRANSLATE_KEY,
                  })
                : selectedValue}
            </Text>
            {loading ? (
              <Loading hideText className="loading-icon" />
            ) : (
              <ChevronDown
                size={22}
                color="#111111"
                style={{
                  transform: `rotate(${open ? '180deg' : 0})`,
                }}
              />
            )}
          </Box>
          <Box
            data-target
            className={classes.selectTarget}
            ref={inputRef}
          ></Box>
          <Portal>
            <Transition
              mounted={Boolean(open && dropdownPos)}
              transition={'fade'}
            >
              {(styles) => (
                <Box
                  ref={selectedValRef}
                  sx={{
                    position: 'absolute',
                    top: dropdownPos?.top,
                    left: dropdownPos?.left,
                    width: dropdownPos?.width,
                    borderRadius: 8,
                    zIndex: selectorZIndex,
                    ...(styles as Sx),
                  }}
                >
                  <Select
                    selectedItem={selectedValue || ''}
                    data={provinceListOptions}
                    onClickItem={(value) => {
                      setSelectedValue(
                        provinceListOptions.filter(
                          (i) => i.value === value,
                        )?.[0]?.label,
                      )
                      setOpen(false)
                      onChangeValue?.(value)
                    }}
                  />
                </Box>
              )}
            </Transition>
          </Portal>
        </Box>
      )}
    </Box>
  )
}
